import { useState, useEffect, createContext, useContext, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (message: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Render Container */}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 max-w-sm w-full pointer-events-none p-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: ToastMessage; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: {
      bg: 'bg-emerald-500 text-white dark:bg-emerald-600',
      icon: <CheckCircle2 className="w-5 h-5 text-white shrink-0" />,
      border: 'border-emerald-400/30',
    },
    error: {
      bg: 'bg-rose-500 text-white dark:bg-rose-600',
      icon: <AlertCircle className="w-5 h-5 text-white shrink-0" />,
      border: 'border-rose-400/30',
    },
    warning: {
      bg: 'bg-amber-500 text-white dark:bg-amber-600',
      icon: <AlertTriangle className="w-5 h-5 text-white shrink-0" />,
      border: 'border-amber-400/30',
    },
    info: {
      bg: 'bg-indigo-500 text-white dark:bg-indigo-600',
      icon: <Info className="w-5 h-5 text-white shrink-0" />,
      border: 'border-indigo-400/30',
    },
  }[toast.type];

  return (
    <div
      className={`pointer-events-auto flex items-center justify-between gap-3 px-4 py-3 rounded-2xl shadow-xl border ${styles.bg} ${styles.border} transition-all duration-300 animate-in fade-in slide-in-from-top-4`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {styles.icon}
        <span className="text-xs md:text-sm font-bold leading-tight drop-shadow-sm">
          {toast.message}
        </span>
      </div>
      <button
        onClick={onClose}
        className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white/80 hover:text-white shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback if used outside provider
    return {
      showToast: (msg: string) => console.log('[Toast]:', msg),
    };
  }
  return context;
}
